import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiEdit2, FiMapPin, FiCalendar, FiTag, FiUser, FiDollarSign } from 'react-icons/fi';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import LoadingSpinner from '../components/LoadingSpinner';
import MapView from '../components/MapView';
import { immobilisationsAPI, qrcodeAPI } from '../services/api';
import { toast } from 'react-toastify';
import ExportPDFButton from '../components/ExportPDFButton';

const ImmobilisationDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [immo, setImmo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDetail();
  }, [id]);

  const loadDetail = async () => {
    try {
      setLoading(true);
      const res = await immobilisationsAPI.getById(id);
      // Backend retourne { data: {...} }
      setImmo(res.data.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
      console.error('Error:', error);
      navigate('/immobilisations');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="page-content" id="page-content"><LoadingSpinner /></div>
        </div>
      </div>
    );
  }

  if (!immo) {
    return (
      <div className="app-layout">
        <Sidebar />
        <div className="main-content">
          <Header />
          <div className="page-content" id="page-content"><p>Immobilisation non trouvée</p></div>
        </div>
      </div>
    );
  }

  const getStateColor = (state) => {
    switch (state) {
      case 'bon': return 'success';
      case 'moyen': return 'warning';
      case 'hors_service': return 'error';
      default: return 'info';
    }
  };

  const getStateLabel = (state) => {
    switch (state) {
      case 'bon': return 'Bon';
      case 'moyen': return 'Moyen';
      case 'hors_service': return 'Hors service';
      default: return state;
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <div className="main-content">
        <Header />
        <div className="page-content" id="page-content">
          <div className="page-header">
            <Link to="/immobilisations" className="btn btn-ghost">
              <FiArrowLeft /> Retour
            </Link>
            <Link to={`/immobilisations/${id}/edit`} className="btn btn-secondary">
              <FiEdit2 /> Éditer
            </Link>
            <ExportPDFButton title="OGP IMMO - Fiche détaillée" fileName="fiche-immobilisation" />
          </div>

          <div className="grid grid-cols-2" style={{ marginTop: '1.5rem' }}>
            {/* Image & Basic Info */}
            <div className="card">
              {immo.photo_url ? (
                <img
                  src={immo.photo_url}
                  alt={immo.nom}
                  style={{ width: '100%', height: '300px', objectFit: 'cover', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{ width: '100%', height: '200px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f0f1f3', borderRadius: 'var(--radius-md)', marginBottom: '1.5rem', fontSize: '3rem' }}>
                  &#128230;
                </div>
              )}

              <h2 style={{ marginBottom: '0.5rem' }}>{immo.nom}</h2>
              <p style={{ fontFamily: 'monospace', fontSize: '0.85rem', color: 'var(--text-light)', marginBottom: '1rem' }}>
                {immo.numero_identification}
              </p>

              <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
                <span className={`badge badge-${getStateColor(immo.etat)}`}>
                  {getStateLabel(immo.etat)}
                </span>
                {immo.enrolle && <span className="badge badge-success">Enrôlé</span>}
              </div>

              {/* QR Code */}
              <div className="card" style={{ padding: '1rem', backgroundColor: 'var(--bg-light)', textAlign: 'center' }}>
                <h4 style={{ marginBottom: '0.75rem' }}>Code QR</h4>
                <img
                  src={qrcodeAPI.generate(immo.id)}
                  alt="QR Code"
                  style={{ width: '180px', height: '180px', margin: '0 auto' }}
                />
                <p style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '0.5rem' }}>
                  {immo.numero_identification}
                </p>
                <a href={qrcodeAPI.label(immo.id)} target="_blank" rel="noopener noreferrer"
                  className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }}>
                  Imprimer l'étiquette
                </a>
              </div>
            </div>

            {/* Details */}
            <div>
              <div className="card" style={{ marginBottom: '1.5rem' }}>
                <h3 style={{ marginBottom: '1.5rem' }}>Informations</h3>
                <div style={{ display: 'grid', gap: '1rem' }}>
                  {[
                    { icon: <FiTag />, label: 'Catégorie', value: immo.categorie_nom || 'N/A' },
                    { icon: null, label: 'Service', value: immo.service_nom || 'N/A' },
                    { icon: <FiUser />, label: 'Responsable', value: immo.responsable || 'N/A' },
                    { icon: <FiDollarSign />, label: 'Valeur', value: immo.valeur ? `${Number(immo.valeur).toLocaleString()} GNF` : 'N/A' },
                    { icon: <FiCalendar />, label: 'Date d\'acquisition', value: immo.date_acquisition ? new Date(immo.date_acquisition).toLocaleDateString('fr-FR') : 'N/A' },
                    { icon: null, label: 'Date de création', value: immo.created_at ? new Date(immo.created_at).toLocaleDateString('fr-FR') : 'N/A' },
                  ].map(({ icon, label, value }) => (
                    <div key={label}>
                      <label style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-light)', display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                        {icon} {label}
                      </label>
                      <p style={{ fontSize: '1rem', marginTop: '0.25rem' }}>{value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {immo.description && (
                <div className="card" style={{ marginBottom: '1.5rem' }}>
                  <h3 style={{ marginBottom: '0.75rem' }}>Description</h3>
                  <p style={{ color: 'var(--text-light)', lineHeight: 1.6 }}>{immo.description}</p>
                </div>
              )}

              {immo.notes && (
                <div className="card">
                  <h3 style={{ marginBottom: '0.75rem' }}>Notes</h3>
                  <p style={{ color: 'var(--text-light)', lineHeight: 1.6 }}>{immo.notes}</p>
                </div>
              )}
            </div>
          </div>

          {/* Map */}
          {immo.latitude && immo.longitude && (
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ marginBottom: '1rem' }}>
                <FiMapPin style={{ display: 'inline', marginRight: '0.5rem' }} />
                Localisation
              </h3>
              <MapView
                immobilisations={[immo]}
                center={[immo.latitude, immo.longitude]}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImmobilisationDetailPage;
